import { BaseApiService } from '../../common/services/baseApiService';
import { API_URL } from '../../common/utils/util';
import {
  AlcanceAsignadoRespuesta,
  AlcanceDeTienda,
  AlcancesSemana,
  ArchivoDescargado,
  AsignarAlcanceRequest,
  Aviso,
  CapturaTienda,
  CondicionDetalle,
  CondicionesDeTienda,
  ContadoresPestanas,
  CopiaSemanaAnterior,
  CuerpoReparto,
  OmitidaReparto,
  RespuestaReparto,
  Duplicado,
  EventoHistorial,
  FiltrosAlcances,
  FiltrosPendientes,
  ListaAlcances,
  ListaPendientesCatalogo,
  MiAlcance,
  PropuestasCatalogo,
  ProponerCondicionRequest,
  ProponerSuplenteRequest,
  ResultadoImportacion,
  ResultadoLote,
  ResultadoResolver,
  ResumenSincronizacion,
  SemanaTiendas,
  SuplenteDetalle,
  SuplentesDeTienda,
  TiendaRef,
  VistaTienda,
} from '../types/metas.types';
import { numeroSemana } from '../utils/semanas';

/**
 * Máximo de tiendas por archivo de Excel. Es el de la API
 * (`MetasColaboradorUseCase.MAX_TIENDAS_EXCEL`): cada tienda arma su plantilla
 * con BUK y el ERP, y con más la API responde 400. Aquí sólo sirve para avisar
 * antes de pedirla.
 */
export const MAX_TIENDAS_EXCEL = 20;

/**
 * Nombre del archivo de la plantilla, armado AQUÍ. La API lo manda en
 * `Content-Disposition`, pero el navegador no deja leer esa cabecera en una
 * petición a otro dominio si la API no la expone por CORS, y no la expone.
 * Mismo formato que la API: `metas-S39-2026-09-21-VAZ1.xlsx` para una tienda,
 * `metas-S39-2026-09-21-11-tiendas.xlsx` para varias.
 */
export function nombrePlantilla(lunes: string, tiendas: TiendaRef[]): string {
  const sufijo = tiendas.length === 1 ? `${tiendas[0].chain}${tiendas[0].num}` : `${tiendas.length}-tiendas`;
  return `metas-S${numeroSemana(lunes)}-${lunes}-${sufijo}.xlsx`;
}

/** `metas-autorizadas-S39-2026-09-21.xlsx`, el mismo nombre que pone la API. */
export function nombreAutorizadas(lunes: string): string {
  return `metas-autorizadas-S${numeroSemana(lunes)}-${lunes}.xlsx`;
}

/** `pendientes-catalogo_2026-09-30.xlsx`: el mismo nombre que pone la API (el día, en hora de México). */
export function nombrePendientesExcel(hoy: string): string {
  return `pendientes-catalogo_${hoy}.xlsx`;
}

/** `alcances-S39-2026-09-21.xlsx`: el mismo nombre que pone la API. */
export function nombreAlcancesExcel(lunes: string): string {
  return `alcances-S${numeroSemana(lunes)}-${lunes}.xlsx`;
}

/**
 * `?semana&cadena&regional&supervisor` de la pestaña Alcances. La semana va
 * siempre (sin ella la API usa la última cerrada, y la pantalla debe pedir la
 * que muestra); cadena, regional y supervisor (21-oct-2026) sólo si filtran.
 */
export function consultaAlcances(lunes: string, f: FiltrosAlcances = {}): string {
  const q = new URLSearchParams();
  q.set('semana', lunes);
  if (f.cadena) q.set('cadena', f.cadena);
  if (f.regional && f.regional.trim()) q.set('regional', f.regional.trim());
  if (f.supervisor && f.supervisor.trim()) q.set('supervisor', f.supervisor.trim());
  return `?${q.toString()}`;
}

/**
 * `?tipo&area&q&resueltos` de la pestaña «Pendientes de catálogo». Sólo lo que
 * filtra: sin filtros, sin `?` (así la ruta de la lista y la del Excel piden lo
 * mismo que se ve).
 */
export function consultaPendientes(f: FiltrosPendientes = {}): string {
  const q = new URLSearchParams();
  if (f.tipo) q.set('tipo', f.tipo);
  if (f.area) q.set('area', f.area);
  if (f.q && f.q.trim()) q.set('q', f.q.trim());
  if (f.resueltos) q.set('resueltos', 'true');
  const s = q.toString();
  return s ? `?${s}` : '';
}

/** Lo que la API manda en el cuerpo de un error. */
export interface CuerpoErrorMetas {
  message?: string | string[];
  errores?: Aviso[];
  duplicados?: Duplicado[];
  /** `POST …/repartir` cuando no aplica (422): por qué, en código, y quién quedaba fuera. */
  codigo?: string;
  omitidas?: OmitidaReparto[];
  statusCode?: number;
  error?: string;
}

/**
 * Error de la API de metas que CONSERVA el cuerpo.
 *
 * `BaseApiService.handleResponse` sólo se queda con `message`, y aquí eso no
 * basta: un 422 al guardar (antes 400) trae la lista de `errores` con la llave de cada
 * persona —para marcar su renglón— y un 409 trae `duplicados` con la tienda
 * donde ya tiene meta. Tirar el cuerpo dejaría al supervisor con una frase
 * larga y sin saber qué renglón corregir.
 */
export class MetasApiError extends Error {
  readonly status: number;
  readonly data: CuerpoErrorMetas;

  constructor(status: number, data: CuerpoErrorMetas) {
    super(MetasApiError.mensajeDe(status, data));
    this.name = 'MetasApiError';
    this.status = status;
    this.data = data;
  }

  /** La validación global de Nest manda `message` como arreglo; lo demás, como texto. */
  static mensajeDe(status: number, data: CuerpoErrorMetas): string {
    // Multer rechaza el archivo antes de llegar al caso de uso, en inglés
    // («File too large»): se dice aquí lo que significa para quien lo sube.
    if (status === 413)
      return 'El archivo pasa de 2 MB: no parece la plantilla. Baja la plantilla desde esta pantalla y súbela.';
    const m = data?.message;
    if (Array.isArray(m) && m.length) return m.join('. ');
    if (typeof m === 'string' && m.trim()) return m;
    if (status === 503)
      return 'BUK o el ERP no respondieron. Intenta en unos minutos.';
    return `La petición falló (${status}).`;
  }

  get errores(): Aviso[] {
    return Array.isArray(this.data?.errores) ? this.data.errores : [];
  }

  get duplicados(): Duplicado[] {
    return Array.isArray(this.data?.duplicados) ? this.data.duplicados : [];
  }

  /** 409: la captura cambió en otra pestaña o la persona ya está en otra tienda. */
  get esConflicto(): boolean {
    return this.status === 409;
  }
}

/**
 * No hubo respuesta HTTP: no se sabe si la API alcanzó a guardar. Quien lo
 * reciba tiene que RECARGAR lo que muestra (la versión pudo cambiar), no sólo
 * enseñar el error.
 */
export class ErrorSinRespuesta extends Error {
  constructor(mensaje: string) {
    super(mensaje);
    this.name = 'ErrorSinRespuesta';
  }
}

/**
 * El archivo elegido, LEÍDO una sola vez. Un `File` apunta al disco: si el
 * supervisor corrige el Excel y lo guarda entre la vista previa y «Guardar»,
 * Chrome aborta la subida (el archivo cambió) y otros navegadores mandan el
 * contenido NUEVO —con una tienda que la vista previa marcó «No se guardará»—.
 * Con los bytes en memoria se guarda exactamente lo que se revisó.
 */
export async function fijarArchivo(archivo: File): Promise<File> {
  const leer = (): Promise<ArrayBuffer> =>
    typeof archivo.arrayBuffer === 'function'
      ? archivo.arrayBuffer()
      : new Promise((ok, mal) => {
          const r = new FileReader();
          r.onload = () => ok(r.result as ArrayBuffer);
          r.onerror = () => mal(r.error);
          r.readAsArrayBuffer(archivo);
        });
  const bytes = await leer();
  return new File([bytes], archivo.name, { type: archivo.type || 'application/octet-stream' });
}

const BASE = `${API_URL}/metas-colaborador`;

const seg = (s: string | number): string => encodeURIComponent(String(s));

/**
 * `/metas-colaborador`. Un método por ruta, como `VendedoresService`.
 *
 * Todas las rutas son de la API: el servidor decide qué tiendas ve cada cuenta
 * por su ALCANCE, así que la pantalla nunca filtra por seguridad, sólo por
 * comodidad.
 */
export class MetasColaboradorService extends BaseApiService {
  private static async pedir<T>(
    ruta: string,
    init: { method: 'GET' | 'POST' | 'PUT' | 'DELETE'; body?: unknown },
  ): Promise<T> {
    const response = await fetch(`${BASE}${ruta}`, {
      method: init.method,
      headers: this.getAuthHeaders(),
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
    if (!response.ok) return this.fallar(response);
    return response.json() as Promise<T>;
  }

  /** Un error de la API como `MetasApiError`, con su cuerpo (401 cierra la sesión). */
  private static async fallar(response: Response): Promise<never> {
    this.handleAuthError(response);
    const data = (await response.json().catch(() => ({}))) as CuerpoErrorMetas;
    throw new MetasApiError(response.status, data ?? {});
  }

  /**
   * Sólo `Authorization`, sin `Content-Type`.
   *
   * ⚠️ `getAuthHeaders` pone `Content-Type: application/json`. En una subida
   * eso rompe el multipart: el navegador tiene que poner el suyo
   * (`multipart/form-data; boundary=…`, con el separador que sólo él conoce) y,
   * con el de JSON, el cuerpo llega etiquetado como JSON y el archivo nunca
   * llega a Multer. En una descarga no hace daño, pero tampoco hay cuerpo que
   * describir.
   */
  private static soloAutorizacion(): Record<string, string> {
    const todas = this.getAuthHeaders() as Record<string, string>;
    const out: Record<string, string> = {};
    Object.keys(todas).forEach((k) => {
      if (k.toLowerCase() !== 'content-type') out[k] = todas[k];
    });
    return out;
  }

  /** Un `.xlsx` de la API como blob, con el nombre que se le da aquí. */
  private static async bajar(ruta: string, nombre: string): Promise<ArchivoDescargado> {
    const response = await fetch(`${BASE}${ruta}`, { method: 'GET', headers: this.soloAutorizacion() });
    if (!response.ok) return this.fallar(response);
    return { nombre, blob: await response.blob() };
  }

  /** El Excel como `multipart/form-data`, en el campo `archivo` que espera la API. */
  private static async subir(ruta: string, archivo: File): Promise<ResultadoImportacion> {
    const form = new FormData();
    form.append('archivo', archivo);
    let response: Response;
    try {
      response = await fetch(`${BASE}${ruta}`, {
        method: 'POST',
        headers: this.soloAutorizacion(),
        body: form,
      });
    } catch {
      // `fetch` sólo lanza cuando NO hubo respuesta HTTP: red caída, un proxy que
      // cortó, o el navegador que se niega a mandar un archivo que cambió en
      // disco. «Failed to fetch» no le dice nada al supervisor.
      throw new ErrorSinRespuesta(
        'No se pudo enviar el archivo: se cortó la conexión o el archivo cambió en disco. Revisa si se guardó algo y vuelve a elegirlo.',
      );
    }
    if (!response.ok) return this.fallar(response);
    return response.json() as Promise<ResultadoImportacion>;
  }

  private static tienda(lunes: string, chain: string, num: number): string {
    return `/semanas/${seg(lunes)}/tiendas/${seg(chain)}/${seg(num)}`;
  }

  static getMiAlcance(): Promise<MiAlcance> {
    return this.pedir<MiAlcance>('/mi-alcance', { method: 'GET' });
  }

  // ------------------------------------------------------------ accesos (admin): antes `/alcances`
  static listarAlcances(): Promise<ListaAlcances> {
    return this.pedir<ListaAlcances>('/accesos', { method: 'GET' });
  }

  static asignarAlcance(req: AsignarAlcanceRequest): Promise<AlcanceAsignadoRespuesta> {
    return this.pedir<AlcanceAsignadoRespuesta>('/accesos', { method: 'POST', body: req });
  }

  static revocarAlcance(id: number): Promise<{ revocado: boolean }> {
    return this.pedir<{ revocado: boolean }>(`/accesos/${seg(id)}`, { method: 'DELETE' });
  }

  // ------------------------------------------------------------ semana
  static listarTiendas(lunes: string): Promise<SemanaTiendas> {
    return this.pedir<SemanaTiendas>(`/semanas/${seg(lunes)}/tiendas`, { method: 'GET' });
  }

  /** Los números de las pestañas (por autorizar, propuestas y pendientes de catálogo), sin armar plantillas. */
  static contadores(lunes: string): Promise<ContadoresPestanas> {
    return this.pedir<ContadoresPestanas>(`/semanas/${seg(lunes)}/contadores`, { method: 'GET' });
  }

  static autorizarLote(
    lunes: string,
    tiendas: { chain: string; num: number; version: number }[],
  ): Promise<ResultadoLote> {
    return this.pedir<ResultadoLote>(`/semanas/${seg(lunes)}/autorizar-lote`, {
      method: 'POST',
      body: { tiendas },
    });
  }

  // ------------------------------------------------------------ una tienda
  static getVistaTienda(lunes: string, chain: string, num: number): Promise<VistaTienda> {
    return this.pedir<VistaTienda>(this.tienda(lunes, chain, num), { method: 'GET' });
  }

  static guardar(
    lunes: string,
    chain: string,
    num: number,
    captura: CapturaTienda,
  ): Promise<VistaTienda> {
    return this.pedir<VistaTienda>(this.tienda(lunes, chain, num), {
      method: 'PUT',
      body: captura,
    });
  }

  static enviar(
    lunes: string,
    chain: string,
    num: number,
    body: { version: number; notaSupervisor?: string | null },
  ): Promise<VistaTienda> {
    return this.pedir<VistaTienda>(`${this.tienda(lunes, chain, num)}/enviar`, {
      method: 'POST',
      body,
    });
  }

  /**
   * «Devolver al gerente» (21-oct-2026, fase B): una tienda «con el
   * supervisor» vuelve a borrador para que el gerente la corrija desde la app.
   * Lo hace quien captura la tienda (supervisor o admin) y exige comentario de
   * al menos `comentarioMin` caracteres (si no, 422 `COMENTARIO_CORTO`); 409 si
   * ya no está con él, cambió la versión o la semana ya no se captura.
   */
  static devolver(
    lunes: string,
    chain: string,
    num: number,
    body: { version: number; comentario: string },
  ): Promise<ResultadoResolver> {
    return this.pedir<ResultadoResolver>(`${this.tienda(lunes, chain, num)}/devolver`, {
      method: 'POST',
      body: { version: body.version, comentario: body.comentario.trim() },
    });
  }

  static autorizar(
    lunes: string,
    chain: string,
    num: number,
    body: { version: number; comentario?: string | null },
  ): Promise<ResultadoResolver> {
    return this.pedir<ResultadoResolver>(`${this.tienda(lunes, chain, num)}/autorizar`, {
      method: 'POST',
      body,
    });
  }

  static rechazar(
    lunes: string,
    chain: string,
    num: number,
    body: { version: number; comentario: string },
  ): Promise<ResultadoResolver> {
    return this.pedir<ResultadoResolver>(`${this.tienda(lunes, chain, num)}/rechazar`, {
      method: 'POST',
      body,
    });
  }

  /**
   * Una tienda AUTORIZADA vuelve a borrador. Sólo administradores (la API lo
   * exige por rol: 403 para cualquier otra cuenta) y con comentario de al menos
   * `comentarioMin` caracteres (si no, 422 `COMENTARIO_CORTO`).
   */
  static reabrir(
    lunes: string,
    chain: string,
    num: number,
    body: { version: number; comentario: string },
  ): Promise<ResultadoResolver> {
    return this.pedir<ResultadoResolver>(`${this.tienda(lunes, chain, num)}/reabrir`, {
      method: 'POST',
      body: { version: body.version, comentario: body.comentario.trim() },
    });
  }

  /**
   * «Copiar semana anterior»: la PROPUESTA de la API (meta o motivo de quien
   * sigue en la plantilla, y a quién omitió y por qué). No guarda nada: la
   * pantalla la aplica a su copia local y se guarda como cualquier cambio.
   * Mismo permiso que capturar la tienda esa semana (403/409/503).
   */
  static copiarAnterior(lunes: string, chain: string, num: number): Promise<CopiaSemanaAnterior> {
    return this.pedir<CopiaSemanaAnterior>(`${this.tienda(lunes, chain, num)}/copiar-anterior`, { method: 'POST' });
  }

  /**
   * «Repartir según venta promedio»: la PROPUESTA de la API para llegar a la
   * meta de tienda, calculada sobre lo GUARDADO (la pantalla guarda antes de
   * pedirla). No guarda nada. 422 con `codigo` y `omitidas` cuando no aplica;
   * 403/409/503 como capturar.
   *
   * «Repartir en partes iguales» (ronda F, 23-oct-2026) es la MISMA ruta con
   * cuerpo: `{ modo: 'iguales' }` da quién puede recibir (paso 1) y
   * `{ modo: 'iguales', keys }`, la propuesta (paso 2). Sin cuerpo no se manda
   * nada: la petición de «según venta promedio» es la de siempre, y una API
   * anterior —que no lee el cuerpo— responde la de promedio (sin `modo`).
   */
  static repartir(lunes: string, chain: string, num: number, cuerpo?: CuerpoReparto): Promise<RespuestaReparto> {
    return this.pedir<RespuestaReparto>(`${this.tienda(lunes, chain, num)}/repartir`, {
      method: 'POST',
      ...(cuerpo ? { body: cuerpo } : {}),
    });
  }

  /** Sólo transiciones; con `todo`, también los guardados, las metas liberadas y la migración. */
  static historial(lunes: string, chain: string, num: number, todo = false): Promise<EventoHistorial[]> {
    return this.pedir<EventoHistorial[]>(`${this.tienda(lunes, chain, num)}/historial${todo ? '?todo=true' : ''}`, {
      method: 'GET',
    });
  }

  // ------------------------------------------------------------ Excel (fase 2)
  /**
   * La plantilla de las tiendas elegidas (`tiendas=VAZ-1,TAF-22`). Siempre se
   * mandan explícitas —también «todas mis tiendas»—: el archivo trae
   * exactamente las que la pantalla muestra y el nombre dice cuántas son.
   */
  static plantillaExcel(lunes: string, tiendas: TiendaRef[]): Promise<ArchivoDescargado> {
    const lista = tiendas.map((t) => `${seg(t.chain)}-${seg(t.num)}`).join(',');
    return this.bajar(`/semanas/${seg(lunes)}/plantilla?tiendas=${lista}`, nombrePlantilla(lunes, tiendas));
  }

  /** Vista previa del Excel: qué cambia y qué errores hay por tienda. NO guarda. */
  static validarExcel(lunes: string, archivo: File): Promise<ResultadoImportacion> {
    return this.subir(`/semanas/${seg(lunes)}/importar/validar`, archivo);
  }

  /**
   * Guarda el Excel: todo o nada por tienda. `tiendas` son las que la VISTA
   * PREVIA dijo que se guardarían: sin la lista, la API vuelve a evaluar el
   * archivo entero y una tienda marcada «No se guardará» (p. ej. porque BUK no
   * respondió) podía guardarse igual si BUK volvía entre medias.
   */
  static importarExcel(lunes: string, archivo: File, tiendas: TiendaRef[]): Promise<ResultadoImportacion> {
    const lista = tiendas.map((t) => `${seg(t.chain)}-${seg(t.num)}`).join(',');
    return this.subir(`/semanas/${seg(lunes)}/importar?tiendas=${lista}`, archivo);
  }

  /** Lo AUTORIZADO de la semana, para `comisiones-spec-semana.cjs`. Sólo admin. */
  static autorizadasExcel(lunes: string): Promise<ArchivoDescargado> {
    return this.bajar(`/semanas/${seg(lunes)}/autorizadas.xlsx`, nombreAutorizadas(lunes));
  }

  // ------------------------------------------------------------ catálogo (fase 3)
  // Las rutas van por TIENDA (no por semana) y las decisiones por ID: una
  // propuesta que se vuelve a proponer cambia de id, y autorizar el viejo da 409.

  private static catalogoDeTienda(chain: string, num: number, que: 'condiciones' | 'suplentes'): string {
    return `/tiendas/${seg(chain)}/${seg(num)}/${que}`;
  }

  /** La vigente en la semana `lunes` (sin él, la API usa la semana en curso), la propuesta abierta y las versiones. */
  static condiciones(chain: string, num: number, lunes?: string): Promise<CondicionesDeTienda> {
    const q = lunes ? `?lunes=${seg(lunes)}` : '';
    return this.pedir<CondicionesDeTienda>(`${this.catalogoDeTienda(chain, num, 'condiciones')}${q}`, { method: 'GET' });
  }

  /** Reemplaza la propuesta abierta, si hay: la anterior queda «retirada» y ésta trae id nuevo. */
  static proponerCondicion(chain: string, num: number, body: ProponerCondicionRequest): Promise<CondicionesDeTienda> {
    return this.pedir<CondicionesDeTienda>(this.catalogoDeTienda(chain, num, 'condiciones'), { method: 'POST', body });
  }

  static autorizarCondicion(id: number, comentario?: string | null): Promise<CondicionDetalle> {
    return this.pedir<CondicionDetalle>(`/condiciones/${seg(id)}/autorizar`, {
      method: 'POST',
      body: { comentario: comentario?.trim() || null },
    });
  }

  /** El comentario es obligatorio: el supervisor lo lee para corregir. */
  static rechazarCondicion(id: number, comentario: string): Promise<CondicionDetalle> {
    return this.pedir<CondicionDetalle>(`/condiciones/${seg(id)}/rechazar`, {
      method: 'POST',
      body: { comentario: comentario.trim() },
    });
  }

  /** Sólo una propuesta abierta; lo autorizado no se borra. */
  static retirarCondicion(id: number): Promise<CondicionDetalle> {
    return this.pedir<CondicionDetalle>(`/condiciones/${seg(id)}`, { method: 'DELETE' });
  }

  /** Los programas abiertos de la tienda (con sus permisos) y los últimos cerrados. */
  static suplentes(chain: string, num: number): Promise<SuplentesDeTienda> {
    return this.pedir<SuplentesDeTienda>(this.catalogoDeTienda(chain, num, 'suplentes'), { method: 'GET' });
  }

  static proponerSuplente(chain: string, num: number, body: ProponerSuplenteRequest): Promise<SuplentesDeTienda> {
    return this.pedir<SuplentesDeTienda>(this.catalogoDeTienda(chain, num, 'suplentes'), {
      method: 'POST',
      body: { ...body, certificadoEn: body.certificadoEn || null },
    });
  }

  static autorizarSuplente(id: number, comentario?: string | null): Promise<SuplenteDetalle> {
    return this.pedir<SuplenteDetalle>(`/suplentes/${seg(id)}/autorizar`, {
      method: 'POST',
      body: { comentario: comentario?.trim() || null },
    });
  }

  static rechazarSuplente(id: number, comentario: string): Promise<SuplenteDetalle> {
    return this.pedir<SuplenteDetalle>(`/suplentes/${seg(id)}/rechazar`, {
      method: 'POST',
      body: { comentario: comentario.trim() },
    });
  }

  /** Termina el bono. Sólo de un programa AUTORIZADO; corregir la fecha se permite. */
  static certificarSuplente(id: number, certificadoEn: string): Promise<SuplenteDetalle> {
    return this.pedir<SuplenteDetalle>(`/suplentes/${seg(id)}/certificar`, { method: 'POST', body: { certificadoEn } });
  }

  /** Sólo una propuesta; un programa autorizado termina al certificarse o a los 3 meses. */
  static retirarSuplente(id: number): Promise<SuplenteDetalle> {
    return this.pedir<SuplenteDetalle>(`/suplentes/${seg(id)}`, { method: 'DELETE' });
  }

  /**
   * Propuestas de condiciones y suplentes que esperan resolución en las tiendas
   * del alcance. Era `GET /pendientes`, que ahora da 404: queda libre para los
   * pendientes de catálogo de la fase 4 (calidad de datos).
   */
  static propuestas(): Promise<PropuestasCatalogo> {
    return this.pedir<PropuestasCatalogo>('/propuestas', { method: 'GET' });
  }

  // ------------------------------------------------------------ pendientes de catálogo (fase 4)
  /**
   * La pestaña «Pendientes de catálogo»: la lista de la ÚLTIMA sincronización
   * que la cuenta ve (el servidor filtra por alcance), los conteos por tipo
   * (sin el filtro de tipo: las tarjetas son ese filtro) y la última corrida.
   */
  static pendientesCatalogo(f: FiltrosPendientes = {}): Promise<ListaPendientesCatalogo> {
    return this.pedir<ListaPendientesCatalogo>(`/catalogo/pendientes${consultaPendientes(f)}`, { method: 'GET' });
  }

  /** «Actualizar»: corre la sincronización ahora. Admin y regional (403 al supervisor); 409 si ya corre otra. */
  static sincronizarPendientes(): Promise<ResumenSincronizacion> {
    return this.pedir<ResumenSincronizacion>('/catalogo/pendientes/sincronizar', { method: 'POST' });
  }

  /** «Exportar Excel»: lo mismo que la pantalla, con los mismos filtros. */
  static pendientesExcel(f: FiltrosPendientes, hoy: string): Promise<ArchivoDescargado> {
    return this.bajar(`/catalogo/pendientes.xlsx${consultaPendientes(f)}`, nombrePendientesExcel(hoy));
  }

  // ------------------------------------------------------------ alcances (fase 5)
  /**
   * Venta real contra meta de una semana: KPIs, bandas y alcance por tienda.
   * El servidor decide qué tiendas ve la cuenta; cadena y regional sólo acotan.
   * Con la semana abierta o el snapshot incompleto responde 200 SIN venta ni %
   * y con el motivo en `aviso`.
   */
  static alcancesSemana(lunes: string, f: FiltrosAlcances = {}): Promise<AlcancesSemana> {
    return this.pedir<AlcancesSemana>(`/alcances${consultaAlcances(lunes, f)}`, { method: 'GET' });
  }

  /** El alcance de cada colaborador de una tienda. 409 si la semana no ha cerrado; 403 si es ajena. */
  static alcanceDeTienda(lunes: string, chain: string, num: number): Promise<AlcanceDeTienda> {
    return this.pedir<AlcanceDeTienda>(`/alcances/tiendas/${seg(chain)}/${seg(num)}?semana=${seg(lunes)}`, {
      method: 'GET',
    });
  }

  /**
   * «Exportar para comisiones»: sólo metas AUTORIZADAS de las tiendas de la
   * cuenta, con los mismos filtros que la pantalla. 409 si la semana no cerró o
   * su venta está incompleta.
   */
  static alcancesExcel(lunes: string, f: FiltrosAlcances = {}): Promise<ArchivoDescargado> {
    return this.bajar(`/alcances/export.xlsx${consultaAlcances(lunes, f)}`, nombreAlcancesExcel(lunes));
  }
}
